import { get, post } from "../api/axios"
import endpoints from "../api/endpoints"
import { store } from "../store"
import { isAdminPortalUser } from "./adminRoles"
import { resolveSessionCity, resolveSessionProject } from "./session"
import { TIMELOG_REASONS } from "./sessionDetail"

export {
  ACTIVE_SESSION_UNLOAD_MESSAGE,
  clearActiveSessionSnapshot,
  getActiveSessionSnapshot,
  markUnloadHandled,
  sendUnloadPause,
  syncActiveSessionSnapshot,
} from "./activeSessionGuard"

const PAUSED_STATES = new Set(["break", "on_break", "paused"])
const RUNNING_STATES = new Set([
  "active",
  "working",
  "running",
  "started",
])

export const isActiveSessionStatus = status => {
  const normalized = String(status || "active").toLowerCase()
  return (
    normalized === "active" ||
    normalized === "running" ||
    normalized === "in_progress"
  )
}

export const resolveShouldPause = (session, userId) => {
  const collabs = Array.isArray(session?.collaborators)
    ? session.collaborators
    : []
  const mine = userId
    ? collabs.find(
        item =>
          String(item.user_id || item.user?.id || "") === String(userId),
      )
    : collabs.find(
        item => String(item.status || "").toLowerCase() === "joined",
      )

  const state = String(mine?.current_state || "").toLowerCase()
  if (PAUSED_STATES.has(state)) return false
  if (RUNNING_STATES.has(state)) return true
  return isActiveSessionStatus(session?.status)
}

export const fetchActiveSession = async () => {
  // Member-only endpoint — admin / city-admin portals do not run live sessions.
  const currentUser = store.getState()?.auth?.currentUser
  if (isAdminPortalUser(currentUser)) {
    return { ok: true, session: null }
  }

  const response = await get(endpoints.getActiveSession, {
    skipErrorToast: true,
  })

  if (!response) {
    return { ok: false, session: null }
  }

  if (!response?.data?.success) {
    return { ok: true, session: null }
  }

  const session = response.data.result
  if (!session?.id) {
    return { ok: true, session: null }
  }

  if (!isActiveSessionStatus(session.status)) {
    return { ok: true, session: null }
  }

  return { ok: true, session }
}

export const pauseActiveSession = async ({
  sessionId,
  shouldPause = false,
  reason = TIMELOG_REASONS.logout,
} = {}) => {
  if (!sessionId) return { ok: false }
  if (!shouldPause) return { ok: true, skipped: true }

  const response = await post(
    endpoints.sessionTimelog(sessionId),
    { action: "break", reason },
    { skipErrorToast: true },
  )

  return { ok: Boolean(response?.data?.success) || !response }
}

export const resolveActiveSessionOpenPayload = session => {
  if (!session?.id) return null
  const project = resolveSessionProject(session)
  const city = resolveSessionCity(session)
  return {
    sessionId: String(session.id),
    projectId: project?.id || null,
    project: project
      ? { id: project.id, title: project.name, name: project.name }
      : null,
    cityId: city?.id || null,
    city: city
      ? { id: city.id, name: city.name, title: city.name, shortName: city.name }
      : null,
  }
}
